import React, { useState, useMemo } from 'react';
import { useSeasonal } from '../api/hooks';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthsUA = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

function SeasonalPage() {
  const { data = {}, isLoading } = useSeasonal();
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categoryNames = useMemo(() => Object.keys(data), [data]);

  const getRowColor = (cat, val) => {
    const values = months.map(m => data[cat]?.[m] || 0);
    const localMax = Math.max(...values);
    const ratio = val / (localMax || 1);
    const intensity = Math.round(255 - ratio * 160);
    return `rgb(${intensity}, ${intensity}, 255)`;
  };

  const chartData = useMemo(() => {
    if (!selectedCategory || !data[selectedCategory]) return null;
    return {
      labels: monthsUA,
      datasets: [
        {
          label: selectedCategory,
          data: months.map(m => data[selectedCategory][m] || 0),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.3
        }
      ]
    };
  }, [selectedCategory, data]);

  const maxMonth = useMemo(() => {
    if (!selectedCategory || !data[selectedCategory]) return null;
    const values = months.map(m => data[selectedCategory][m] || 0);
    const maxIdx = values.indexOf(Math.max(...values));
    return monthsUA[maxIdx];
  }, [selectedCategory, data]);

  const minMonth = useMemo(() => {
    if (!selectedCategory || !data[selectedCategory]) return null;
    const values = months.map(m => data[selectedCategory][m] || 0);
    const minIdx = values.indexOf(Math.min(...values));
    return monthsUA[minIdx];
  }, [selectedCategory, data]);

  return (
    <div className="p-6" style={{ zoom: '85%' }}>
      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
        <span role="img" aria-label="calendar"></span> Сезонність продажів
      </h2>

      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <div className="w-8 h-8 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="overflow-auto border rounded text-xs">
            <table className="min-w-full text-center">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">КАТЕГОРІЯ</th>
                  {monthsUA.map(month => (
                    <th key={month} className="p-2">{month}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categoryNames.map((cat, i) => (
                  <tr
                    key={i}
                    className="cursor-pointer hover:bg-blue-50"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    <td className="p-2 font-medium text-left text-black whitespace-nowrap">{cat}</td>
                    {months.map((month, j) => (
                      <td
                        key={j}
                        style={{ backgroundColor: getRowColor(cat, data[cat]?.[month] || 0) }}
                        className="p-1"
                      >
                        {(data[cat]?.[month] || 0).toLocaleString()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedCategory && chartData && (
            <div className="bg-white p-4 rounded shadow max-w-4xl mx-auto">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-base font-semibold text-blue-800">Сезонність: {selectedCategory}</h3>
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-sm text-gray-500 hover:underline"
                >Закрити</button>
              </div>

              <div className="flex gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded w-full text-center">
                  <p className="text-xs text-blue-600">Найактивніший місяць</p>
                  <p className="text-lg font-semibold">{maxMonth}</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded w-full text-center">
                  <p className="text-xs text-yellow-600">Найменш активний місяць</p>
                  <p className="text-lg font-semibold">{minMonth}</p>
                </div>
              </div>

              <div className="h-64">
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' }
                    },
                    scales: {
                      y: {
                        title: { display: true, text: 'Продажі' }
                      },
                      x: {
                        title: { display: true, text: 'Місяць' }
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SeasonalPage;
